// PRD 14 §3.4 — read-only detail view of a single version.
// Renders status badge + meta grid (version / dates / revision note / author)
// followed by the HTML body. Edit / Publish / Delete actions land in Slices 5/6.

import { useTranslation } from 'react-i18next';

import type { TermVersion } from '../types/terms.types';
import { VersionMetaGrid } from './VersionMetaGrid';

interface VersionReadViewProps {
  version: TermVersion;
}

export function VersionReadView({ version }: VersionReadViewProps) {
  const { t } = useTranslation('terms');
  const isArchived = version.status === 'ARCHIVED';
  // PRD 14 §3.4 / §8.3 — a PUBLISHED version is read-only too, but for a different
  // reason than ARCHIVED: it is still in force, so the notice has to name the way
  // forward ([새 버전 등록]) rather than just state that the version is dead. Without
  // it the operator reads the missing Edit button as a permission problem (SHCT-705).
  const isPublished = version.status === 'PUBLISHED';

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      {isArchived ? (
        <div
          role="status"
          style={{
            marginBottom: 'var(--space-4)',
            padding: '10px 12px',
            background: '#F3F4F6',
            color: '#6B7280',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {t('detail.archivedNotice')}
        </div>
      ) : null}

      {isPublished ? (
        <div
          role="status"
          style={{
            marginBottom: 'var(--space-4)',
            padding: '10px 12px',
            background: '#EFF6FF',
            color: '#1D4ED8',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {t('detail.publishedNotice')}
        </div>
      ) : null}

      <div style={{ marginBottom: 'var(--space-4)' }}>
        <VersionMetaGrid version={version} />
      </div>

      <div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--muted-foreground)',
            marginBottom: 6,
          }}
        >
          {t('detail.fields.content')}
        </div>
        {/* Contract §1.2 / §5: BE owns sanitization of stored HTML; FE renders
            the returned `content` verbatim. */}
        <div
          className="terms-detail-body mg-rte-render"
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: '12px 14px',
            background: '#fff',
            fontSize: 14,
            lineHeight: 1.6,
            minHeight: 240,
            whiteSpace: 'pre-wrap',
          }}
          dangerouslySetInnerHTML={{ __html: version.content }}
        />
      </div>
    </div>
  );
}
